import React, { useMemo, useState } from 'react'
import ReactFlow, { Background, Controls, MiniMap } from 'reactflow'
import 'reactflow/dist/style.css'
import { Loader2, Network, Hammer, AlertTriangle, RefreshCw } from 'lucide-react'
import { useProjects } from '../hooks/useRag'
import { useMindmap, useBuildMindmap } from '../hooks/useResearch'

// Brand-ish colors per node type (light + dark friendly).
const TYPE_STYLE = {
  concept: { bg: '#caa24a', fg: '#1a1714' },
  term: { bg: '#22b8cf', fg: '#08232a' },
  person: { bg: '#b18bd8', fg: '#1c1426' },
  question: { bg: '#e8a13b', fg: '#231603' },
  action: { bg: '#7bc47f', fg: '#0c2110' },
  document: { bg: '#8a8f98', fg: '#16181c' },
}
const COLUMN_ORDER = ['concept', 'term', 'person', 'question', 'action', 'document']
const MAX_NODES = 140
const ROWS_PER_COL = 14 // wrap each type-block into sub-columns of this many rows
const COL_W = 232
const ROW_H = 58
const BLOCK_GAP = 56 // horizontal gap between type blocks

// Map the engine graph (nodes dict + edges list) into reactflow elements. Nodes
// are grouped by type into blocks; each block wraps into sub-columns so a large
// type doesn't become one unreadable tall stack. No layout dependency needed.
function toFlow(data, { showDocuments }) {
  const nodesIn = Object.values(data?.nodes || {})
  const filtered = nodesIn.filter((n) => showDocuments || n.node_type !== 'document')
  // keep the most-mentioned nodes if we exceed the cap
  const ranked = [...filtered].sort((a, b) => (b.mention_count || 0) - (a.mention_count || 0))
  const kept = ranked.slice(0, MAX_NODES)
  const keptIds = new Set(kept.map((n) => n.id))

  // Group kept nodes by type, preserving the mention ranking within each.
  const byType = {}
  for (const n of kept) (byType[n.node_type || 'concept'] ??= []).push(n)

  const nodes = []
  let blockX = 0
  for (const type of COLUMN_ORDER) {
    const list = byType[type]
    if (!list || list.length === 0) continue
    const s = TYPE_STYLE[type] || TYPE_STYLE.concept
    list.forEach((n, i) => {
      const subCol = Math.floor(i / ROWS_PER_COL)
      const row = i % ROWS_PER_COL
      nodes.push({
        id: n.id,
        data: { label: n.label || n.id, type },
        position: { x: blockX + subCol * COL_W, y: row * ROW_H },
        style: {
          background: s.bg,
          color: s.fg,
          border: 'none',
          borderRadius: 8,
          fontSize: 11,
          padding: '6px 10px',
          width: 200,
        },
      })
    })
    const subCols = Math.ceil(list.length / ROWS_PER_COL)
    blockX += subCols * COL_W + BLOCK_GAP
  }

  const edges = (data?.edges || [])
    .filter((e) => keptIds.has(e.source_id) && keptIds.has(e.target_id))
    .map((e) => ({
      id: e.id,
      source: e.source_id,
      target: e.target_id,
      label: e.relationship,
      animated: false,
      style: { stroke: '#caa24a55' },
      labelStyle: { fontSize: 9, fill: '#9a9a9a' },
    }))

  return { nodes, edges }
}

export default function KnowledgeMap({ onPickConcept }) {
  const projectsQuery = useProjects()
  const [project, setProject] = useState('')
  const [showDocuments, setShowDocuments] = useState(false)
  const [building, setBuilding] = useState(false)
  const [progress, setProgress] = useState(null)
  const [err, setErr] = useState(null)

  React.useEffect(() => {
    if (!project && projectsQuery.data?.current_project) setProject(projectsQuery.data.current_project)
  }, [project, projectsQuery.data])

  const map = useMindmap(project, true)
  const buildMut = useBuildMindmap()
  const projects = projectsQuery.data?.projects ?? []

  const flow = useMemo(
    () => toFlow(map.data?.data, { showDocuments }),
    [map.data, showDocuments]
  )

  const build = async () => {
    if (!project) return
    if (
      !window.confirm(
        'Building the knowledge map runs the LLM over every document (≈one call per chunk) and can take a while / incur cost on large projects. Continue?'
      )
    )
      return
    setBuilding(true)
    setErr(null)
    try {
      let done = false
      let guard = 0
      while (!done && guard++ < 300) {
        const r = await buildMut.mutateAsync({ project, limit: 5 })
        setProgress({ analyzed: r.analyzed_total, remaining: r.pending_remaining, nodes: r.total_nodes })
        done = r.done
      }
    } catch (e) {
      setErr(e.message)
    } finally {
      setBuilding(false)
    }
  }

  const stats = map.data
  const hasGraph = stats?.exists && stats.total_nodes > 0

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3">
        <select
          value={project}
          onChange={(e) => setProject(e.target.value)}
          className="h-9 rounded-lg px-3 text-sm bg-white border border-parchment-border text-ink focus:outline-none focus:ring-2 focus:ring-gold/50 dark:bg-midnight-700 dark:border-midnight-600 dark:text-mist"
        >
          <option value="">Select a project…</option>
          {projects.map((p) => (
            <option key={p.name} value={p.name}>{p.name}</option>
          ))}
        </select>

        <button
          onClick={build}
          disabled={!project || building}
          className="inline-flex items-center gap-2 px-4 h-9 rounded-lg text-sm font-medium bg-gradient-to-r from-gold to-spark text-midnight-900 hover:opacity-90 disabled:opacity-40 transition"
        >
          {building ? <Loader2 className="w-4 h-4 animate-spin" /> : <Hammer className="w-4 h-4" />}
          {building ? 'Building…' : hasGraph ? 'Continue building' : 'Build knowledge map'}
        </button>

        {hasGraph && (
          <button
            onClick={() => map.refetch()}
            className="inline-flex items-center gap-1.5 text-sm text-ink-soft hover:text-ink dark:text-mist/50 dark:hover:text-mist"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        )}

        <label className="flex items-center gap-1.5 text-sm text-ink-soft dark:text-mist/60 ml-auto">
          <input type="checkbox" checked={showDocuments} onChange={(e) => setShowDocuments(e.target.checked)} />
          Show documents
        </label>
      </div>

      {building && progress && (
        <div className="text-sm text-gold-deep dark:text-gold">
          Analyzed {progress.analyzed} docs · {progress.remaining} remaining · {progress.nodes} nodes so far…
        </div>
      )}
      {err && (
        <div className="flex items-center gap-2 text-sm text-red-600">
          <AlertTriangle className="w-4 h-4" /> {err}
        </div>
      )}

      {/* Graph / empty state */}
      <div className="rounded-xl border border-parchment-border dark:border-midnight-600 overflow-hidden" style={{ height: 560 }}>
        {hasGraph ? (
          <ReactFlow
            nodes={flow.nodes}
            edges={flow.edges}
            fitView
            minZoom={0.1}
            onNodeClick={(_e, node) => node.data?.type !== 'document' && onPickConcept?.(node.data.label)}
            proOptions={{ hideAttribution: true }}
          >
            <Background color="#caa24a22" gap={24} />
            <Controls showInteractive={false} />
            <MiniMap pannable zoomable nodeColor={(n) => (TYPE_STYLE[n.data?.type] || TYPE_STYLE.concept).bg} />
          </ReactFlow>
        ) : (
          <div className="h-full flex flex-col items-center justify-center text-center px-8 text-ink-soft dark:text-mist/50">
            <Network className="w-10 h-10 mb-3 opacity-50" />
            {map.isLoading ? (
              'Loading…'
            ) : project ? (
              <p>No knowledge map yet for <span className="text-gold-deep dark:text-gold">{project}</span>. Click “Build knowledge map” to extract concepts and relationships from its documents.</p>
            ) : (
              'Select a project to view or build its knowledge map.'
            )}
          </div>
        )}
      </div>

      {hasGraph && (
        <div className="flex flex-wrap items-center gap-3 text-xs text-ink-soft dark:text-mist/50">
          <span>{stats.total_nodes} nodes · {stats.total_edges} edges · {stats.analyzed_documents} docs analyzed</span>
          {Object.entries(stats.node_types || {}).map(([t, c]) => (
            <span key={t} className="inline-flex items-center gap-1">
              <span className="w-2 h-2 rounded-full" style={{ background: (TYPE_STYLE[t] || TYPE_STYLE.concept).bg }} />
              {t} {c}
            </span>
          ))}
          <span className="ml-auto italic">Tip: click a concept to ask about it.</span>
        </div>
      )}
    </div>
  )
}
